import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@store'
import { storageURL, mediaUrl } from '@config'
import publicApiClient from '@services/api/publicClient'
import likesCommentsService from '@services/api/likesComments'
import apiClient from '@services/api/client'
import toast from 'react-hot-toast'
import { encodeId, decodeId } from '@utils/idCipher'

function VideoDetailPage() {
  const { id: encodedId } = useParams()
  const id = decodeId(encodedId) // id réel pour les appels API
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  const [video, setVideo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isPlaying, setIsPlaying] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [relatedVideos, setRelatedVideos] = useState([])
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [activeTab, setActiveTab] = useState('comments')
  const [isLiked, setIsLiked] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [subscribersCount, setSubscribersCount] = useState(0)
  const [error, setError] = useState(null)
  const [loadingComment, setLoadingComment] = useState(false)
  const incrementedIdRef = useRef(null)
  const playerRef = useRef(null)

  // Récupérer les données de la vidéo depuis l'API
  useEffect(() => {
    const fetchVideo = async () => {
      try {
        setLoading(true)
        setError(null)

        const res = await publicApiClient.get(`/posts/${id}`)
        const p = res.data || res

        if (!p || !p.post_id) {
          setError('Vidéo non trouvée')
          setLoading(false)
          return
        }

        // Trouver le fichier vidéo et la miniature dans fichiers[]
        const videoFile = p.fichiers?.find(f => /\.(mp4|mov|avi|webm)$/i.test(f.chemin))
        const thumbFile = p.fichiers?.find(f => /\.(jpg|jpeg|png|gif|webp)$/i.test(f.chemin))

        const clientPhoto = p.client?.photo_profil
        const avatarUrl = clientPhoto
          ? (clientPhoto.startsWith('http') ? clientPhoto : mediaUrl(clientPhoto))
          : null

        const videoData = {
          id:          p.post_id,
          title:       p.libele,
          description: p.description || 'Aucune description disponible',
          videoUrl:    videoFile ? mediaUrl(videoFile.chemin) : null,
          thumbnail:   thumbFile ? mediaUrl(thumbFile.chemin) : null,
          views:       p.vues || 0,
          likes:       p.post_likes_count || 0,
          dislikes: 0,
          comments: p.post_comments_count ?? 0,
          shares:   0,
          uploadDate:  p.created_at,
          category:    p.category?.libele_categorie || 'Non classé',
          tags:        [],
          clientId:    p.post_client_id,
          author: {
            id:       p.client?.client_id || 0,
            name:     [p.client?.nom, p.client?.prenom].filter(Boolean).join(' ') || 'Utilisateur',
            avatar:   avatarUrl,
            subscribers: 0,
            verified: false
          },
          product: {
            id:    p.post_id,
            name:  p.libele,
            price: p.prix,
          }
        }

        setVideo(videoData)

        // Vérifier si l'utilisateur a liké (connexion requise)
        if (user?.client_id || user?.id) {
          try {
            const likeStatus = await likesCommentsService.checkLike(id, user?.id || user?.client_id)
            setIsLiked(likeStatus.isLiked || false)
          } catch (err) {
            console.error('Erreur lors de la vérification du like:', err)
          }
        }

        // Récupérer les commentaires (stocker bruts comme ProductDetailPage)
        try {
          const commentsData = await likesCommentsService.getComments(id)
          if (commentsData.success) {
            const list = commentsData.comments || []
            setComments(list)
            setVideo(prev => prev ? { ...prev, comments: list.length } : prev)
          }
        } catch (err) {
          console.error('Erreur lors du chargement des commentaires:', err)
        }

        // Récupérer les vidéos similaires
        try {
          const relatedData = await publicApiClient.get('/posts', {
            params: { post_categorie_id: p.post_categorie_id, type: 1, status: 2 }
          })
          const items = relatedData.data || relatedData.posts || (Array.isArray(relatedData) ? relatedData : [])
          const formattedRelated = items
            .filter(post => post.post_id !== parseInt(id))
            .slice(0, 8)
            .map(post => {
              // Miniature = première image ; sinon on affichera une frame de la vidéo
              const img = post.fichiers?.find(f => /\.(jpg|jpeg|png|gif|webp)$/i.test(f.chemin))
              const vid = post.fichiers?.find(f => /\.(mp4|mov|avi|webm)$/i.test(f.chemin))
              return {
                id: post.post_id || post.id,
                title: post.libele || post.name,
                thumbnail: img ? mediaUrl(img.chemin) : null,
                videoUrl: vid ? mediaUrl(vid.chemin) : null,
                author: post.client_name || [post.client?.prenom, post.client?.nom].filter(Boolean).join(' ') || 'Utilisateur',
                views: post.vues || 0,
                uploadDate: formatDate(post.created_at)
              }
            })
          setRelatedVideos(formattedRelated)
        } catch (err) {
          console.error('Erreur lors du chargement des vidéos similaires:', err)
        }

        // Abonnement — lecture depuis localStorage (comme ProductDetailPage)
        const currentUserId = user?.id || user?.client_id
        if (currentUserId && videoData.author?.id) {
          const subKey = `sub_${currentUserId}_${videoData.author.id}`
          setIsSubscribed(localStorage.getItem(subKey) === 'true')
        }
        setSubscribersCount(videoData.author?.subscribers || 0)

      } catch (err) {
        console.error('Erreur lors du chargement de la vidéo:', err)
        setError('Erreur lors du chargement de la vidéo')
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchVideo()
    }
  }, [id])

  // Incrément de vue — guard contre double-appel StrictMode
  useEffect(() => {
    if (!id || incrementedIdRef.current === id) return
    incrementedIdRef.current = id
    publicApiClient.post(`/posts/${id}/view`).catch(() => {})
  }, [id])

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue'
    
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now - date)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'Aujourd\'hui'
    if (diffDays === 1) return 'Hier'
    if (diffDays < 7) return `Il y a ${diffDays} jours`
    if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} semaines`
    if (diffDays < 365) return `Il y a ${Math.floor(diffDays / 30)} mois`
    return `Il y a ${Math.floor(diffDays / 365)} ans`
  }

  const handleLike = async () => {
    const clientId = user?.id || user?.client_id
    if (!clientId) {
      toast.error('Vous devez être connecté pour liker cette vidéo')
      return
    }
    try {
      if (isLiked) {
        await likesCommentsService.unlikePost(id, clientId)
        setIsLiked(false)
        setVideo(prev => ({ ...prev, likes: Math.max(0, prev.likes - 1) }))
      } else {
        await likesCommentsService.likePost(id, clientId)
        setIsLiked(true)
        setVideo(prev => ({ ...prev, likes: prev.likes + 1 }))
      }
    } catch (err) {
      console.error('Erreur lors du like:', err)
    }
  }

  const handleShare = () => {
    setShowShareModal(true)
  }

  const handleSubscribe = async () => {
    if (!isAuthenticated) { navigate('/login'); return }
    const clientId = user?.id || user?.client_id
    const abonneId = video?.author?.id
    if (!abonneId) return
    if (Number(clientId) === Number(abonneId)) return
    try {
      await apiClient.post('/subscription/subscribe', {
        action: 'toggle',
        client_id: clientId,
        abonne_id: abonneId
      })
      const newState = !isSubscribed
      setIsSubscribed(newState)
      setSubscribersCount(prev => newState ? prev + 1 : Math.max(0, prev - 1))
      localStorage.setItem(`sub_${clientId}_${abonneId}`, newState)
      toast.success(newState ? 'Abonné avec succès' : 'Désabonné avec succès')
    } catch (err) {
      toast.error('Erreur lors de l\'abonnement')
    }
  }

  const handleCommentSubmit = async (e) => {
    e.preventDefault()
    if (!newComment.trim()) return
    if (!isAuthenticated) { navigate('/login'); return }
    const currentUserId = user?.id || user?.client_id
    setLoadingComment(true)
    try {
      const response = await likesCommentsService.addComment(id, newComment, currentUserId)
      if (response.success) {
        setComments(prev => [response.comment, ...prev])
        setNewComment('')
        setVideo(prev => prev ? { ...prev, comments: (prev.comments || 0) + 1 } : prev)
      }
    } catch (err) {
      console.error('Erreur lors de l\'ajout du commentaire:', err)
    } finally {
      setLoadingComment(false)
    }
  }

  if (loading) {
    return (
      <div className="home-font min-h-screen page-pattern">
        <div className="w-[94%] md:w-[80%] mx-auto py-8 grid lg:grid-cols-[minmax(0,1fr)_380px] gap-8">
          <div className="space-y-4">
            <div className="aspect-video rounded-md skeleton-shimmer" />
            <div className="h-32 rounded-md skeleton-shimmer" />
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-24 rounded-md skeleton-shimmer" />)}
          </div>
        </div>
      </div>
    )
  }

  if (error || !video) {
    return (
      <div className="home-font min-h-screen page-pattern flex items-center justify-center px-4">
        <div className="text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-md" style={GOLD_PILL}>
            <Svg d={PATHS.play} className="w-7 h-7" />
          </span>
          <h2 className="mt-5 text-2xl font-extrabold text-[#002A4E]">Vidéo introuvable</h2>
          <p className="mt-2 text-slate-500">{error || 'Cette vidéo n\'existe pas ou a été supprimée.'}</p>
          <button
            onClick={() => navigate('/kudhi-market')}
            className="mt-6 rounded-md bg-[#FFD700] px-6 py-3 font-bold text-[#002A4E] transition-colors hover:bg-[#F5C800]"
          >
            Retour au Market
          </button>
        </div>
      </div>
    )
  }

  const isOwner = Number(user?.id || user?.client_id) === Number(video.author.id)
  const authorInitials = video.author.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'
  const shareUrl = `${window.location.origin}/video/${encodeId(video.id)}`
  const hasVideo = video.videoUrl && (video.videoUrl.includes('.mp4') || video.videoUrl.includes('.webm') || video.videoUrl.includes('video'))
  const priceLabel = (() => {
    const n = parseFloat(String(video.product?.price ?? '').replace(/[^0-9.]/g, ''))
    return n > 0 ? new Intl.NumberFormat('fr-FR').format(n) + ' FCFA' : null
  })()

  return (
    <div className="home-font min-h-screen page-pattern">
      <div className="w-[94%] md:w-[80%] mx-auto py-6 lg:py-8">

        {/* Fil d'Ariane */}
        <nav className="mb-5 flex items-center gap-2 text-sm text-slate-500">
          <button onClick={() => navigate(-1)} className="flex h-9 w-9 items-center justify-center rounded-md bg-white text-[#002A4E] ring-1 ring-slate-200 transition-colors hover:ring-amber-300" aria-label="Retour">
            <Svg d={PATHS.chevLeft} className="w-4 h-4" stroke={2.2} />
          </button>
          <button onClick={() => navigate('/kudhi-market')} className="font-medium hover:text-[#002A4E]">KUDHI Market</button>
          <Svg d={PATHS.chevRight} className="w-3.5 h-3.5 text-slate-300" />
          <span className="text-slate-400">Vidéos</span>
          <Svg d={PATHS.chevRight} className="w-3.5 h-3.5 text-slate-300" />
          <span className="truncate font-semibold text-[#002A4E]">{video.title}</span>
        </nav>

        <div className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_380px] gap-6 lg:gap-8">

          {/* ===== Colonne principale ===== */}
          <div className="space-y-5 min-w-0">

            {/* Lecteur */}
            <div className="group relative aspect-video overflow-hidden rounded-md bg-[#001A33] shadow-[0_20px_50px_-25px_rgba(0,42,78,0.6)]">
              {hasVideo ? (
                <>
                  <video
                    ref={playerRef}
                    className="h-full w-full"
                    controls={isPlaying}
                    preload="metadata"
                    poster={video.thumbnail || undefined}
                    src={video.thumbnail ? video.videoUrl : `${video.videoUrl}#t=0.1`}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  >
                    <source src={video.videoUrl} type="video/mp4" />
                    Votre navigateur ne supporte pas la lecture de vidéos.
                  </video>
                  {!isPlaying && (
                    <button
                      onClick={() => playerRef.current?.play()}
                      className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-[#001A33]/70 via-transparent to-transparent"
                      aria-label="Lire la vidéo"
                    >
                      <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[#FFD700] text-[#002A4E] shadow-[0_10px_40px_rgba(255,215,0,0.5)] ring-8 ring-white/15 transition-transform group-hover:scale-110">
                        <svg className="ml-1 h-9 w-9" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.14v13.72a1 1 0 001.52.85l11.03-6.86a1 1 0 000-1.7L9.52 4.29A1 1 0 008 5.14z" /></svg>
                      </span>
                    </button>
                  )}
                </>
              ) : (
                <img src={video.thumbnail} alt={video.title} className="h-full w-full object-contain" />
              )}
            </div>

            {/* Titre, statistiques, actions */}
            <div className="rounded-md bg-white p-5 sm:p-6 ring-1 ring-slate-200/70">
              <h1 className="text-xl sm:text-2xl font-extrabold leading-tight tracking-tight text-[#002A4E]">{video.title}</h1>

              <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                  <span className="inline-flex items-center gap-1.5"><Svg d={PATHS.eye} className="w-4 h-4" />{video.views.toLocaleString('fr-FR')} vue{video.views > 1 ? 's' : ''}</span>
                  <span className="inline-flex items-center gap-1.5"><Svg d={PATHS.clock} className="w-4 h-4" />{new Date(video.uploadDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                  {video.category && <span className="rounded-md bg-[#FFF8D6] px-2 py-0.5 text-xs font-bold text-[#B8860B]">{video.category}</span>}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleLike}
                    className={`inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold ring-1 transition-colors ${
                      isLiked ? 'bg-red-50 text-red-600 ring-red-200' : 'bg-white text-slate-600 ring-slate-200 hover:text-red-600 hover:ring-red-200'
                    }`}
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d={PATHS.heart} /></svg>
                    {video.likes}
                  </button>
                  <button
                    onClick={handleShare}
                    className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 ring-1 ring-slate-200 transition-colors hover:text-[#002A4E] hover:ring-amber-300"
                  >
                    <Svg d={PATHS.share} className="w-5 h-5" />
                    Partager
                  </button>
                </div>
              </div>

              {/* Auteur */}
              <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4 rounded-md bg-slate-50 p-4">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  {video.author.avatar ? (
                    <img
                      src={video.author.avatar}
                      alt={video.author.name}
                      className="h-12 w-12 flex-shrink-0 rounded-full object-cover ring-2 ring-[#FFD700]"
                      onError={e => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex' }}
                    />
                  ) : null}
                  <span
                    className="h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-base font-bold"
                    style={{ ...GOLD_PILL, display: video.author.avatar ? 'none' : 'flex' }}
                  >
                    {authorInitials}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-bold text-[#002A4E]">{video.author.name}</p>
                    <p className="text-sm text-slate-500">{subscribersCount.toLocaleString('fr-FR')} abonné{subscribersCount > 1 ? 's' : ''}</p>
                  </div>
                </div>
                {!isOwner && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (!isAuthenticated) { navigate('/login'); return }
                        navigate('/messages', {
                          state: {
                            openConversationWith: {
                              id:     video.author?.id,
                              name:   video.author?.name,
                              avatar: video.author?.avatar,
                              article: { name: video.title, url: `/video/${encodedId}`, image: video.thumbnail },
                            }
                          }
                        })
                      }}
                      className="inline-flex flex-1 sm:flex-none items-center justify-center gap-2 rounded-md bg-[#002A4E] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#003866]"
                    >
                      <Svg d={PATHS.message} className="w-5 h-5" />
                      Message
                    </button>
                    <button
                      onClick={handleSubscribe}
                      className={`flex-1 sm:flex-none rounded-md px-5 py-2.5 text-sm font-bold transition-colors ${
                        isSubscribed ? 'bg-slate-200 text-slate-600 hover:bg-slate-300' : 'bg-[#FFD700] text-[#002A4E] hover:bg-[#F5C800]'
                      }`}
                    >
                      <span className="inline-flex items-center justify-center gap-1.5"><Svg d={isSubscribed ? PATHS.check : PATHS.userPlus} className="w-4 h-4" stroke={2} />{isSubscribed ? 'Abonné' : 'S\'abonner'}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Onglets */}
            <div className="rounded-md bg-white ring-1 ring-slate-200/70">
              <div className="flex gap-6 border-b border-slate-100 px-5 sm:px-6">
                {[
                  ['comments', `Commentaires (${video.comments})`, 'message'],
                  ['info', 'Description', 'doc'],
                  ...(priceLabel ? [['product', 'Offre', 'tag']] : []),
                ].map(([key, label, icon]) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`relative py-4 text-sm font-bold transition-colors ${
                      activeTab === key ? 'text-[#002A4E]' : 'text-slate-400 hover:text-[#002A4E]'
                    }`}
                  >
                    <span className="inline-flex items-center gap-2"><Svg d={PATHS[icon]} className="w-4 h-4" />{label}</span>
                    {activeTab === key && <span className="absolute inset-x-0 -bottom-px h-[3px] rounded-full bg-[#F5B700]" />}
                  </button>
                ))}
              </div>

              <div className="p-5 sm:p-6">
                {activeTab === 'comments' && (
                  <div className="space-y-5">
                    {isAuthenticated ? (
                      <form onSubmit={handleCommentSubmit} className="flex gap-3">
                        {(() => {
                          const userPhoto = user?.photo_profil
                            ? (user.photo_profil.startsWith('http') ? user.photo_profil : mediaUrl(user.photo_profil))
                            : null
                          const userInitials = [user?.prenom, user?.nom].filter(Boolean).map(w => w[0]).join('').toUpperCase() || 'V'
                          return (
                            <div className="relative h-10 w-10 flex-shrink-0">
                              <span className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold" style={GOLD_PILL}>{userInitials}</span>
                              {userPhoto && <img src={userPhoto} alt="" className="absolute inset-0 h-10 w-10 rounded-full object-cover" onError={e => { e.target.style.display = 'none' }} />}
                            </div>
                          )
                        })()}
                        <div className="flex-1">
                          <input
                            type="text"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Ajouter un commentaire…"
                            className="w-full rounded-md border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-[#002A4E] placeholder:text-slate-400 focus:border-amber-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-amber-100"
                          />
                          {newComment && (
                            <div className="mt-2 flex justify-end gap-2">
                              <button type="button" onClick={() => setNewComment('')} className="rounded-md px-4 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-100">Annuler</button>
                              <button type="submit" disabled={loadingComment} className="rounded-md bg-[#FFD700] px-4 py-2 text-sm font-bold text-[#002A4E] transition-colors hover:bg-[#F5C800] disabled:opacity-50">
                                {loadingComment ? 'Publication…' : 'Commenter'}
                              </button>
                            </div>
                          )}
                        </div>
                      </form>
                    ) : (
                      <div className="flex items-center gap-3 rounded-md bg-[#FFF8D6] p-4 text-sm text-[#002A4E]">
                        <Svg d={PATHS.message} className="w-5 h-5 flex-shrink-0 text-[#B8860B]" />
                        <span>
                          <button onClick={() => navigate('/login')} className="font-bold underline underline-offset-2">Connectez-vous</button>
                          {' '}pour laisser un commentaire.
                        </span>
                      </div>
                    )}

                    {comments.length === 0 ? (
                      <p className="py-6 text-center text-sm text-slate-400">Aucun commentaire. Soyez le premier !</p>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {comments.map((comment, idx) => {
                          const clientName = comment.client?.name || comment.author || 'Anonyme'
                          const clientPhoto = comment.client?.photo
                            ? (comment.client.photo.startsWith('http') ? comment.client.photo : mediaUrl(comment.client.photo))
                            : null
                          const initials = clientName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
                          const commentText = comment.comment_text || comment.text || comment.content || ''
                          const commentDate = comment.created_at
                            ? new Date(comment.created_at).toLocaleDateString('fr-FR')
                            : comment.date || ''
                          return (
                            <div key={comment.id ?? idx} className="flex gap-3 py-4">
                              {clientPhoto ? (
                                <img src={clientPhoto} alt={clientName} className="h-10 w-10 flex-shrink-0 rounded-full object-cover" />
                              ) : (
                                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold" style={GOLD_PILL}>{initials}</span>
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-baseline gap-2">
                                  <span className="font-bold text-[#002A4E]">{clientName}</span>
                                  <span className="text-xs text-slate-400">{commentDate}</span>
                                </div>
                                <p className="mt-1 text-sm leading-relaxed text-slate-600">{commentText}</p>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'info' && (
                  <p className="whitespace-pre-line leading-relaxed text-slate-600">{video.description}</p>
                )}

                {activeTab === 'product' && priceLabel && (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-md bg-[#FFF8D6] p-5">
                    <div>
                      <p className="font-bold text-[#002A4E]">{video.product.name}</p>
                      <p className="mt-1 text-2xl font-extrabold text-[#002A4E]">{priceLabel}</p>
                    </div>
                    {!isOwner && (
                      <button
                        onClick={() => {
                          if (!isAuthenticated) { navigate('/login'); return }
                          navigate('/messages', {
                            state: { openConversationWith: { id: video.author?.id, name: video.author?.name, avatar: video.author?.avatar, article: { name: video.title, url: `/video/${encodedId}`, image: video.thumbnail } } }
                          })
                        }}
                        className="rounded-md bg-[#002A4E] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#003866]"
                      >
                        Contacter le vendeur
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ===== Vidéos similaires ===== */}
          <aside className="lg:sticky lg:top-28 self-start">
            <div className="rounded-md bg-white ring-1 ring-slate-200/70">
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                <SectionTitle icon="video" className="text-base">À voir aussi</SectionTitle>
                <span className="text-xs font-semibold text-slate-400">{relatedVideos.length} vidéo{relatedVideos.length > 1 ? 's' : ''}</span>
              </div>
              <div className="max-h-[calc(100vh-12rem)] space-y-1 overflow-y-auto p-2">
                {relatedVideos.length === 0 ? (
                  <p className="px-3 py-8 text-center text-sm text-slate-400">Aucune autre vidéo dans cette catégorie.</p>
                ) : relatedVideos.map((rv) => (
                  <button
                    key={rv.id}
                    onClick={() => { navigate(`/video/${encodeId(rv.id)}`); window.scrollTo(0, 0) }}
                    className="group flex w-full gap-3 rounded-md p-2 text-left transition-colors hover:bg-slate-50"
                  >
                    <RelatedThumb thumbnail={rv.thumbnail} videoUrl={rv.videoUrl} title={rv.title} />
                    <div className="min-w-0 flex-1 py-0.5">
                      <h3 className="text-sm font-bold leading-snug text-[#002A4E] line-clamp-2 transition-colors group-hover:text-[#B8860B]">{rv.title}</h3>
                      <p className="mt-1 truncate text-xs text-slate-500">{rv.author}</p>
                      <p className="mt-0.5 text-xs text-slate-400">{rv.views.toLocaleString('fr-FR')} vues · {rv.uploadDate}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Modal de partage */}
      {showShareModal && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in" onClick={() => setShowShareModal(false)} />
          <div className="relative w-full sm:max-w-md rounded-t-md sm:rounded-md bg-white p-6 shadow-2xl animate-scale-in">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="text-xl font-extrabold text-[#002A4E]">Partager cette vidéo</h3>
              <button onClick={() => setShowShareModal(false)} aria-label="Fermer" className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100">
                <Svg d={PATHS.close} className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6 grid grid-cols-3 gap-3">
              {[
                { label: 'WhatsApp', bg: '#25D366', href: `https://wa.me/?text=${encodeURIComponent(`${video.title} — ${shareUrl}`)}`, icon: <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor"><path d={WHATSAPP_PATH} /></svg> },
                { label: 'Facebook', bg: '#1877F2', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, icon: <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg> },
                { label: 'X', bg: '#000000', href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(video.title)}`, icon: <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg> },
              ].map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-2 rounded-md p-3 transition-colors hover:bg-slate-50">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full text-white" style={{ background: s.bg }}>{s.icon}</span>
                  <span className="text-xs font-semibold text-slate-600">{s.label}</span>
                </a>
              ))}
            </div>

            <div className="flex gap-2">
              <input type="text" value={shareUrl} readOnly className="min-w-0 flex-1 rounded-md bg-slate-100 px-4 py-2.5 text-sm text-slate-600" />
              <button
                onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success('Lien copié !') }}
                className="rounded-md bg-[#FFD700] px-4 py-2.5 text-sm font-bold text-[#002A4E] transition-colors hover:bg-[#F5C800]"
              >
                Copier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Éléments d'interface de la page détail vidéo
// ---------------------------------------------------------------------------

const GOLD_PILL = { background: 'linear-gradient(135deg, #FFE45C 0%, #FFD700 50%, #F5B700 100%)', color: '#002A4E' }

const PATHS = {
  doc: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z',
  tag: 'M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z M6 6h.008v.008H6V6z',
  userPlus: 'M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z',
  check: 'M4.5 12.75l6 6 9-13.5',
  list: 'M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z',
  user: 'M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z',
  grid: 'M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25a2.25 2.25 0 01-2.25-2.25v-2.25z',
  video: 'M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9A2.25 2.25 0 0013.5 5.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z',  eye: 'M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  heart: 'M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z',
  message: 'M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z',
  share: 'M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z',
  clock: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
  play: 'M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 010 1.972l-11.54 6.347a1.125 1.125 0 01-1.667-.986V5.653z',
  close: 'M6 18L18 6M6 6l12 12',
  chevLeft: 'M15.75 19.5L8.25 12l7.5-7.5',
  chevRight: 'M8.25 4.5l7.5 7.5-7.5 7.5',
}

const WHATSAPP_PATH = 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z'

const Svg = ({ d, className = 'w-5 h-5', stroke = 1.8 }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
)

const formatDuration = (s) => {
  if (!Number.isFinite(s) || s <= 0) return null
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${m}:${String(sec).padStart(2, '0')}`
}

// Miniature d'une vidéo similaire : image si disponible, sinon première image de la vidéo.
// La durée réelle est lue dans les métadonnées de la vidéo.
const RelatedThumb = ({ thumbnail, videoUrl, title }) => {
  const [duration, setDuration] = useState(null)
  return (
    <div className="relative h-20 w-36 flex-shrink-0 overflow-hidden rounded-md bg-[#001A33]">
      {thumbnail ? (
        <img src={thumbnail} alt={title} className="h-full w-full object-cover" />
      ) : videoUrl ? (
        <video
          src={`${videoUrl}#t=1`}
          preload="metadata"
          muted
          playsInline
          className="h-full w-full object-cover"
          onLoadedMetadata={(e) => setDuration(formatDuration(e.target.duration))}
        />
      ) : null}
      {thumbnail && videoUrl && (
        <video src={videoUrl} preload="metadata" muted className="hidden" onLoadedMetadata={(e) => setDuration(formatDuration(e.target.duration))} />
      )}
      <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFD700] text-[#002A4E]">
          <svg className="ml-0.5 h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.14v13.72a1 1 0 001.52.85l11.03-6.86a1 1 0 000-1.7L9.52 4.29A1 1 0 008 5.14z" /></svg>
        </span>
      </span>
      {duration && (
        <span className="absolute bottom-1 right-1 rounded bg-black/75 px-1.5 py-0.5 text-[10px] font-semibold text-white tabular-nums">{duration}</span>
      )}
    </div>
  )
}

// Titre de section avec pastille d'icône
const SectionTitle = ({ icon, children, className = '' }) => (
  <h2 className={`flex items-center gap-2.5 text-lg font-extrabold text-[#002A4E] ${className}`}>
    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md" style={GOLD_PILL}>
      <Svg d={PATHS[icon]} className="w-4 h-4" stroke={2} />
    </span>
    {children}
  </h2>
)

export default VideoDetailPage
